import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/stores/components/StoresToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';

import {
  createDefaultStoreForm,
  formatStoreAddress,
  hasMeaningfulStoreFormInput,
  hasStoreFormChanged,
  InventoryStoreAddress,
  InventoryStoreFormData,
  InventoryStoreRow,
  storeStatusOptions,
} from '../models/StoreModel';
import { createStore, deleteStore, getStore, updateStore } from '../redux';
import StoreForm from './StoreForm';
import StoreList, { StoreListHandle } from './StoreList';

const buildFormMetadata = (formData: InventoryStoreFormData, storeId?: string) => ({
  companyId: formData.company_id,
  formData,
  initialFormData: formData,
  storeId,
});

const updateFormMetadata = (
  formData: InventoryStoreFormData,
  storeId?: string,
  initialFormData?: InventoryStoreFormData
) => ({
  companyId: formData.company_id,
  formData,
  initialFormData: initialFormData || formData,
  storeId,
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return '';
  }
  return companyId;
};

const normalizeStoreAddressObject = (address: InventoryStoreFormData['address']): InventoryStoreAddress => ({
  address_line: String(address?.address_line || ''),
  province: String(address?.province || ''),
  ward: String(address?.ward || ''),
  free_address: String(address?.free_address || ''),
});

export default function StoreManagementModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const listRef = useRef<StoreListHandle>(null);

  const [rows, setRows] = useState<InventoryStoreRow[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const {
    canCreate: canCreateStores,
    canUpdate: canUpdateStores,
    canDelete: canDeleteStores,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.store',
    activeCompanyFilter,
    updateTabTypes: ['update-store'],
  });

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách kho', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateStores) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateStores]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_store_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateStores),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-store', 'update-store'],
    allowMultipleTabTypes: ['new-store', 'update-store'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || resolveCompanyIdForQuery(selectedCompanyId);
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `Kho #${currentTabs.filter((tab) => tab.type === 'new-store').length + 1}`,
        type: 'new-store',
        closable: true,
        metadata: buildFormMetadata(createDefaultStoreForm(baseCompanyId)),
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryStoreFormData | undefined;
  const activeStoreId = activeTabData?.metadata?.storeId as string | undefined;

  const refreshStoreList = useCallback(() => {
    listRef.current?.requestRefresh();
  }, []);

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateFormData = (updates: Partial<InventoryStoreFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeStoreId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    const initialFormData = activeTabData.metadata?.initialFormData as InventoryStoreFormData | undefined;
    updateTab(activeTabData.id, { metadata: updateFormMetadata(nextFormData, activeStoreId, initialFormData) });
    handleUnsavedChangesUpdate(
      isUpdateMode ? hasStoreFormChanged(nextFormData, initialFormData) : hasMeaningfulStoreFormInput(nextFormData),
      activeTabData.id
    );
  };

  const openExistingStore = (row: InventoryStoreRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết kho.', severity: 'error' });
      return;
    }
    guardAndRun(() => {
      void (async () => {
        try {
          const detail = await getStore(row.id);
          openTab({
            id: Date.now() + Math.floor(Math.random() * 1000),
            title: detail.code,
            type: 'update-store',
            closable: true,
            entityId: detail.id,
            metadata: buildFormMetadata(
              {
                code: detail.code,
                name: detail.name,
                status: detail.status,
                company_id: detail.company_id,
                address:
                  detail.address && typeof detail.address === 'object'
                    ? detail.address
                    : {
                        address_line: typeof detail.address === 'string' ? detail.address : '',
                        province: '',
                        ward: '',
                        free_address: '',
                      },
              },
              detail.id
            ),
          });
        } catch (error: any) {
          setSnackbarMessage({
            message: error?.response?.data?.detail || 'Không thể tải chi tiết kho.',
            severity: 'error',
          });
        }
      })();
    });
  };

  const handleSave = async () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activeStoreId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật kho.', severity: 'error' });
      return;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo kho.', severity: 'error' });
      return;
    }

    setSaveLoading(true);
    try {
      const addressObj = normalizeStoreAddressObject(activeFormData.address);
      const payload = {
        code: activeFormData.code.trim(),
        name: activeFormData.name.trim(),
        status: activeFormData.status,
        address: formatStoreAddress(addressObj),
        address_obj: addressObj,
        company: activeFormData.company_id,
      };

      const savedStore = activeStoreId ? await updateStore(activeStoreId, payload) : await createStore(payload);

      handleUnsavedChangesUpdate(false, activeTabData.id);
      setSnackbarMessage({ message: `Đã lưu ${savedStore.code}.`, severity: 'success' });
      refreshStoreList();
      handleCloseTab(activeTabData.id);
      return;

    } catch (error: any) {
      setSnackbarMessage({
        message: error?.response?.data?.errors?.[0]?.detail || error?.response?.data?.detail || 'Không thể lưu kho.',
        severity: 'error',
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleBulkDelete = async (storeIds: string[]) => {
    if (!storeIds.length) return;

    if (!canDeleteStores) {
      setSnackbarMessage({ message: 'Bạn không có quyền xóa kho.', severity: 'error' });
      return;
    }

    try {
      await Promise.all(storeIds.map((storeId) => deleteStore(storeId)));
      setSelectedStoreIds([]);
      setSnackbarMessage({ message: `Đã xóa ${storeIds.length} kho.`, severity: 'success' });
      refreshStoreList();
    } catch (error: any) {
      setSnackbarMessage({
        message: error?.response?.data?.detail || 'Không thể xóa các kho đã chọn.',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleBulkDisable = async (storeIds: string[]) => {
    if (!storeIds.length) return;

    if (!canUpdateStores) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật kho.', severity: 'error' });
      return;
    }

    try {
      const selectedRows = rows.filter((row) => storeIds.includes(row.id));
      await Promise.all(
        selectedRows.map((row) =>
          updateStore(row.id, {
            code: row.code,
            name: row.name,
            status: 'inactive',
            address: formatStoreAddress(row.address),
            address_obj: row.address && typeof row.address === 'object' ? row.address : undefined,
            company: row.company_id,
          })
        )
      );
      setSelectedStoreIds([]);
      setSnackbarMessage({ message: `Đã ngừng hoạt động ${selectedRows.length} kho.`, severity: 'success' });
      refreshStoreList();
    } catch (error: any) {
      setSnackbarMessage({
        message: error?.response?.data?.detail || 'Không thể cập nhật các kho đã chọn.',
        severity: 'error',
      });
      throw error;
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryStoreFormData | undefined;
              const tabInitialFormData = tab.metadata?.initialFormData as InventoryStoreFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);
              const tabIsUpdateMode = tab.type === 'update-store';
              const tabCanEdit = tab.type === 'new-store' ? tabPermissions.canCreate : tabPermissions.canUpdate;
              const tabHasChanges = tabIsUpdateMode
                ? hasStoreFormChanged(tabFormData, tabInitialFormData)
                : hasMeaningfulStoreFormInput(tabFormData);

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={storeStatusOptions}
                            selectedCount={selectedStoreIds.length}
                            onDelete={() => listRef.current?.requestDelete()}
                            canDelete={canDeleteStores}
                          />
                        }
                        dataTable={
                          <StoreList
                            ref={listRef}
                            companyId={listScopeCompanyId || selectedCompanyId}
                            searchQuery={searchQuery}
                            status={filters.status}
                            onSelectRow={openExistingStore}
                            onSelectionChange={setSelectedStoreIds}
                            onRowsChange={setRows}
                            onFetchError={(message) => setSnackbarMessage({ message, severity: 'error' })}
                            onBulkDelete={handleBulkDelete}
                            onBulkDisable={handleBulkDisable}
                            canUpdate={canUpdateStores}
                            canDelete={canDeleteStores}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', overflowX: 'hidden', p: 2, boxSizing: 'border-box' }}>
                      <StoreForm
                        formData={tabFormData}
                        canEdit={tabCanEdit}
                        statusOptions={storeStatusOptions.filter((option) => option.id !== 'all')}
                        onChangeForm={(updates) => {
                          if (!tabCanEdit) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            const nextFormData = { ...tabFormData, ...updates };
                            updateTab(tab.id, {
                              metadata: updateFormMetadata(
                                nextFormData,
                                tab.metadata?.storeId as string | undefined,
                                tabInitialFormData
                              ),
                            });
                            handleUnsavedChangesUpdate(
                              tabIsUpdateMode ? hasStoreFormChanged(nextFormData, tabInitialFormData) : hasMeaningfulStoreFormInput(nextFormData),
                              tab.id
                            );
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={() => { void handleSave(); }}
                        onCancel={() => handleCloseTab(tab.id)}
                        saving={saveLoading}
                        submitDisabled={saveLoading || !tabCanEdit || !tabHasChanges}
                        isUpdateMode={tabIsUpdateMode}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity={snackbarMessage?.severity || 'success'} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
